import { supabase } from '@/lib/supabase';
import { toUserMessage } from '@/lib/errors';
import { log } from '@/lib/logger';
import type { Tables, TablesInsert, TablesUpdate } from '@/types/database';
import { ok, err, type ApiResponse } from './_base';

export type Empresa = Tables<'empresas'>;
export type EmpresaInsert = TablesInsert<'empresas'>;
export type EmpresaUpdate = TablesUpdate<'empresas'>;

/** Lista empresas visibles para el usuario logueado (RLS filtra). */
export async function listEmpresas(): Promise<ApiResponse<Empresa[]>> {
  const { data, error } = await supabase.from('empresas').select('*').order('nombre');
  if (error) {
    log('error', 'empresas.list', error.message);
    return err(error.code ?? 'LIST_FAILED', toUserMessage(error));
  }
  return ok(data ?? []);
}

/** Obtiene una empresa por id. */
export async function getEmpresa(id: string): Promise<ApiResponse<Empresa | null>> {
  const { data, error } = await supabase
    .from('empresas')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) {
    log('error', 'empresas.get', error.message, { id });
    return err(error.code ?? 'GET_FAILED', toUserMessage(error));
  }
  return ok(data);
}

/** Crea una empresa (solo APEX). */
export async function createEmpresa(input: EmpresaInsert): Promise<ApiResponse<Empresa>> {
  const { data, error } = await supabase
    .from('empresas')
    .insert(input)
    .select('*')
    .single();
  if (error) {
    log('error', 'empresas.create', error.message);
    return err(error.code ?? 'CREATE_FAILED', toUserMessage(error));
  }
  return ok(data);
}

/** Actualiza campos de una empresa. */
export async function updateEmpresa(
  id: string,
  patch: EmpresaUpdate,
): Promise<ApiResponse<Empresa>> {
  const { data, error } = await supabase
    .from('empresas')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) {
    log('error', 'empresas.update', error.message, { id });
    return err(error.code ?? 'UPDATE_FAILED', toUserMessage(error));
  }
  return ok(data);
}

/**
 * Sube un logo al bucket empresa-logos y devuelve la URL pública con cache
 * buster. El path usa `<empresaId>/logo.<ext>`. RLS del bucket autoriza a
 * APEX para cualquier empresa y a PARTNER para la suya.
 */
export async function uploadLogoEmpresa(
  empresaId: string,
  file: File,
): Promise<ApiResponse<string>> {
  const rawExt = (file.name.split('.').pop() ?? 'png').toLowerCase();
  const ext = ['png', 'jpg', 'jpeg', 'webp', 'svg'].includes(rawExt) ? rawExt : 'png';
  const path = `${empresaId}/logo.${ext}`;
  const { error: upErr } = await supabase.storage
    .from('empresa-logos')
    .upload(path, file, { upsert: true, contentType: file.type || `image/${ext}` });
  if (upErr) {
    log('error', 'empresas.uploadLogo', upErr.message, { empresaId });
    return err('UPLOAD_FAILED', toUserMessage(upErr));
  }
  const { data } = supabase.storage.from('empresa-logos').getPublicUrl(path);
  // Cache-buster simple: ?v=<timestamp>. Sin esto el browser sigue mostrando el logo viejo.
  const publicUrl = `${data.publicUrl}?v=${Date.now()}`;
  return ok(publicUrl);
}
